import {Point} from "./geometry";
import {CharValue, ItemData, ItemPoints, ItemPointsIndex} from "./item_types";
import * as utils from "./item_utils";
import {Result} from "./models";

interface ViewPort {
  startX: number;
  endX: number;
  startY: number;
  endY: number;
}

export class Item {
  public draw(item: any): string {
    if (item.tool.ctor === "Text") {
      return "";
    } else {
      const parsedData = this.parse(item);
      const width = item.input.end.x - item.input.start.x;
      const height = item.input.end.y - item.input.start.y;
      return this.generateString(width, height, this.sortData(parsedData.points));
    }
  };

  public isHit(item: any, point: Point): boolean {
    if (item.tool.ctor === "Text") {
      const lengths = item.text.split("\n").map((l) => l.length);
      const line = point.y - item.input.start.y;
      const pointInLine = point.x - item.input.start.x;
      return pointInLine >= 0 && pointInLine <= lengths[line] - 1;
    } else {
      return !!this.parse(item).index[point.x + "_" + point.y];
    }
  }

  public buildResult(items: any[]): Result {
    const that = this;
    let values: Array<[number, any, ItemData]> = [];
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      values.push([item.id, item, this.parse(this.shiftTo0(item))]);
    }
    values = values.sort((a, b) => {
      return parseInt(a[1].z, 10) - parseInt(b[1].z, 10);
    });

    function generateIndex(viewport: ViewPort): {[k: string]: CharValue} {
      const index = {};
      for (let i = 0; i < values.length; i += 1) {
        const item = values[i][1];
        const indexCache = values[i][2];
        for (const k in indexCache.index) {
          if (indexCache.index.hasOwnProperty(k)) {
            const match = k.match(/(\d+)_(\d+)/);
            if (match) {
              const x = parseInt(match[1], 10);
              const y = parseInt(match[2], 10);
              const startX = Math.min(item.input.start.x, item.input.end.x) - viewport.startX;
              const startY = Math.min(item.input.start.y, item.input.end.y) - viewport.startY;
              index[(startX + x) + "_" + (startY + y)] = indexCache.index[k];
            }
          }
        }
      }
      return index;
    }

    function generatePoints(index: ItemPointsIndex): ItemPoints {
      const points: ItemPoints = [];
      for (const k in index) {
        if (index.hasOwnProperty(k)) {
          const match = k.match(/(\d+)_(\d+)/);
          if (match) {
            const x = parseInt(match[1], 10);
            const y = parseInt(match[2], 10);
            points.push([[x, y], index[k]]);
          }
        }
      }
      return that.sortData(points);
    }

    function stringLength(str: string): number {
      return str.length;
    }

    function getViewport(): ViewPort {
      const result = {startX: Infinity, startY: Infinity, endX: 0, endY: 0};
      for (let i = 0; i < values.length; i += 1) {
        const item = values[i][1];
        const rect = itemWithTextBoundingRect(item);
        result.startX = Math.min(result.startX, rect.x);
        result.startY = Math.min(result.startY, rect.y);
        result.endX = Math.max(result.endX, rect.x + rect.width);
        result.endY = Math.max(result.endY, rect.y + rect.height);
      }
      return result;
    }

    function addText(viewport: ViewPort, width: number, data: string): string {
      for (let i = 0; i < values.length; i += 1) {
        const item = values[i][1];
        const text = item.text;
        const widthItem = Math.abs(item.input.start.x - item.input.end.x);
        const heightItem = Math.abs(item.input.start.y - item.input.end.y);
        const startX = Math.min(item.input.start.x, item.input.end.x) - viewport.startX;
        const startY = Math.min(item.input.start.y, item.input.end.y) - viewport.startY;
        const widthCenterItem = startX + widthItem / 2;
        const heightCenterItem = startY + heightItem / 2;
        let pos;
        let line;
        if (text && text !== "") {
          let textArray;
          if (item.tool === "Text") {
            textArray = text.split("\n");
            for (let j = 0; j < textArray.length; j += 1) {
              line = textArray[j];
              pos = (startY + j) * (width + 1) + startX;
              data = data.substr(0, pos) +
                  data.substr(pos, line.length).replace(/.*/, line) +
                  data.substr(pos + line.length, data.length - (pos + line.length));
            }
          } else {
            const splittedText = text.split("\n");
            const widthText = Math.max.apply(null, splittedText.map(stringLength));
            const heightText = splittedText.length;
            const startXText = Math.ceil(widthCenterItem - widthText / 2);
            const startYText = Math.ceil(heightCenterItem - heightText / 2);
            for (let j = 0; j < splittedText.length; j += 1) {
              line = splittedText[j];
              pos = (startYText + j) * (width + 1) + startXText;
              data = data.substr(0, pos) +
                  data.substr(pos + 1, line.length).replace(/.*/, line) +
                  data.substr(pos + line.length, data.length - (pos + line.length));
            }
          }
        }
      }
      return data;
    }

    const viewport = getViewport();
    const result = generateIndex(viewport);
    const points = generatePoints(result);
    const width = viewport.endX - viewport.startX + 1;
    const height = viewport.endY - viewport.startY + 1;
    let content = that.generateString(width, height, points);
    content = addText(viewport, width, content);
    return {width: width, height: height, content: content};
  }

  private elmListToArray(list: any): any[] {
    if (!list.ctor || list.ctor === "[]") {
      return [];
    } else {
      const result = [list._0];
      let v = list._1;
      while (v.ctor === "::") {
        result.push(v._0);
        v = v._1;
      }
      return result;
    }
  }

  // Bresenham's line algorithm
  // http://en.wikipedia.org/wiki/Bresenham%27s_line_algorithm
  // data - [x1: num, y1: num, x2: num, y2: num], firstChar: string (e.g. "-" or "|")
  private buildLine(
      data: [number, number, number, number],
      firstChars: string[] = [],
      lastChars: string[] = []
  ): ItemData {
    let [x1, y1, x2, y2] = data;
    const dx = Math.abs(x2 - x1);
    const dy = Math.abs(y2 - y1);
    const sx = (x1 < x2 ? 1 : -1);
    const sy = (y1 < y2 ? 1 : -1);
    const sym = (dx < dy ? "|" : "-");
    const slash = (sx === sy ? "\\" : "/");
    let err = dx - dy;

    const result = {points: [], index: {}};
    while (x1 !== x2 || y1 !== y2) {
      const e2 = err << 1;
      let newErr;
      if (e2 > -dy && e2 < dx) {
        newErr = err - dy + dx;
      } else if (e2 > -dy) {
        newErr = err - dy;
      } else if (e2 < dx) {
        newErr = err + dx;
      } else {
        newErr = err;
      }
      const newX1 = (e2 > -dy ? x1 + sx : x1);
      const newY1 = (e2 < dx ? y1 + sy : y1);
      const newSym = ((sym === "|" && newX1 !== x1) || (sym === "-" && newY1 !== y1) ? slash : sym);

      utils.add(result, x1, y1, newSym);

      x1 = newX1;
      y1 = newY1;
      err = newErr;
    }
    utils.add(result, x2, y2, sym);

    firstChars = firstChars || [];
    lastChars = lastChars || [];

    firstChars.forEach((firstChar, i) => {
      const points = result.points[i];
      if (points != null) {
        const point = points[0];
        utils.replace(result, point[0], point[1], firstChar);
      }
    });

    lastChars.forEach((lastChar, i) => {
      const points = result.points[result.points.length - 1 - i];
      if (points != null) {
        const point = points[0];
        utils.replace(result, point[0], point[1], lastChar);
      }
    });

    return result;
  }

  private buildRect(data: [number, number, number, number]): ItemData {
    const x1 = Math.min(data[0], data[2]);
    const y1 = Math.min(data[1], data[3]);
    const x2 = Math.max(data[0], data[2]);
    const y2 = Math.max(data[1], data[3]);
    const result = {points: [], index: {}};
    utils.add(result, x1, y1, "+");
    if (x1 !== x2) {
      utils.add(result, x2, y1, "+");
    }
    if (y1 !== y2) {
      utils.add(result, x1, y2, "+");
    }
    if (x1 !== x2 && y1 !== y2) {
      utils.add(result, x2, y2, "+");
    }
    if (Math.abs(x2 - x1) > 1) {
      utils.concat(result, this.buildLine([x1 + 1, y1, x2 - 1, y1], ["-"]));
      if (Math.abs(y2 - y1) > 0) {
        utils.concat(result, this.buildLine([x1 + 1, y2, x2 - 1, y2], ["-"]));
      }
    }
    if (Math.abs(y2 - y1) > 1) {
      utils.concat(result, this.buildLine([x1, y1 + 1, x1, y2 - 1], ["|"]));
      if (Math.abs(x2 - x1) > 0) {
        utils.concat(result, this.buildLine([x2, y1 + 1, x2, y2 - 1], ["|"]));
      }
    }
    return result;
  }

  private buildRectLine(
      data: [number, number, number, number],
      direction: "Vertical" | "Horizontal" = "Horizontal",
      firstChars: string[] = [],
      lastChars: string[] = []
  ): ItemData {
    const [x1, y1, x2, y2] = data;
    const result = {points: [], index: {}};
    if (direction === "Horizontal") {
      if (Math.abs(x2 - x1) > 0) {
        utils.concat(result, this.buildLine([x1, y1, x2 > x1 ? (x2 - 1) : (x2 + 1), y1], firstChars));
      }
      utils.add(result, x2, y1, "+");
      if (Math.abs(y2 - y1) > 0) {
        utils.concat(result, this.buildLine([x2, y2 > y1 ? (y1 + 1) : (y1 - 1), x2, y2], ["|"], lastChars));
      }
    } else {
      if (Math.abs(y2 - y1) > 0) {
        utils.concat(result, this.buildLine([x1, y1, x1, y2 > y1 ? (y2 - 1) : (y2 + 1)], firstChars));
      }
      utils.add(result, x1, y2, "+");
      if (Math.abs(x2 - x1) > 0) {
        utils.concat(result, this.buildLine([x2 > x1 ? (x1 + 1) : (x1 - 1), y2, x2, y2], ["-"], lastChars));
      }
    }
    return result;
  }

  private sortData(data: ItemPoints): ItemPoints {
    return (data || []).sort((a, b) => {
      let result;
      if (a[0][1] > b[0][1]) {
        result = 1;
      } else if (a[0][1] < b[0][1]) {
        result = -1;
      } else {
        if (a[0][0] > b[0][0]) {
          result = 1;
        } else if (a[0][0] < b[0][0]) {
          result = -1;
        } else {
          result = 0;
        }
      }
      return result;
    });
  }

  private generateString(width: number, height: number, points: ItemPoints): string {
    let line = 0;
    let pos = 0;
    let result = "";
    for (let i in points) {
      if (points.hasOwnProperty(i)) {
        const dataPoint = points[i];
        const point = dataPoint[0];
        const x = point[0];
        const y = point[1];
        const character = dataPoint[1];
        while (y !== line) {
          result += utils.repeatString(" ", width - pos);
          result += "\n";
          pos = 0;
          line += 1;
        }
        result += utils.repeatString(" ", x - pos);
        result += character.v;
        pos = x + 1;
        line = y;
      }
    }
    while (line < height) {
      result += utils.repeatString(" ", width - pos);
      pos = 0;
      line += 1;
      if (line < height) {
        result += "\n";
      }
    }
    return result;
  }

  private isConnected(item: any, isFirst: boolean): boolean {
    let isConnected = false;
    const connected = this.elmListToArray(item.connected);
    for (const i in connected) {
      if (connected.hasOwnProperty(i)) {
        const el = connected[i];
        isConnected = el && el.ctor && el.ctor === (isFirst ? "StartEdge" : "EndEdge");
        if (isConnected) {
          return isConnected;
        }
      }
    }
    return isConnected;
  }

  private getEdgeChar(item: any, isFirst: boolean): string[] {
    const tool = item.tool.ctor || item.tool;
    switch (tool) {
      case "Line": {
        const result: string[] = [];
        if (this.isConnected(item, isFirst)) {
          result.push("+");
        }
        if ((isFirst && (item.lineEdges.start.ctor || item.lineEdges.start) === "EdgeArrow")
            || (!isFirst && (item.lineEdges.end.ctor || item.lineEdges.end) === "EdgeArrow")) {
          const aspectRatio = (item.input.end.x - item.input.start.x) / (item.input.end.y - item.input.start.y);
          const isHorizontallyFlipped = item.input.start.x > item.input.end.x;
          const isVerticallyFlipped = item.input.start.y > item.input.end.y;
          if (Math.abs(aspectRatio) > 1) {
            result.push(isHorizontallyFlipped ? (isFirst ? ">" : "<") : (isFirst ? "<" : ">"));
          } else {
            result.push(isVerticallyFlipped ? (isFirst ? "v" : "^") : (isFirst ? "^" : "v"));
          }
        }
        return result;
      }
      case "RectLine": {
        const result: string[] = [];
        if (this.isConnected(item, isFirst)) {
          result.push("+");
        }
        if ((isFirst && (item.lineEdges.start.ctor || item.lineEdges.start) === "EdgeArrow")
            || (!isFirst && (item.lineEdges.end.ctor || item.lineEdges.end) === "EdgeArrow")) {
          const direction = (item.direction._0 && item.direction._0.ctor) || item.direction;
          const isHorizontallyFlipped = item.input.start.x > item.input.end.x;
          const isVerticallyFlipped = item.input.start.y > item.input.end.y;
          if (direction === "Horizontal") {
            result.push(isHorizontallyFlipped
              ? (isVerticallyFlipped ? (isFirst ? ">" : "^") : (isFirst ? ">" : "v"))
              : (isVerticallyFlipped ? (isFirst ? "<" : "^") : (isFirst ? "<" : "v")));
          } else {
            result.push(isHorizontallyFlipped
              ? (isVerticallyFlipped ? (isFirst ? "v" : "<") : (isFirst ? "^" : "<"))
              : (isVerticallyFlipped ? (isFirst ? "v" : ">") : (isFirst ? "^" : ">")));
          }
        }
        return result;
      }
      default:
        throw ("Unknown tool: " + tool);
    }
  }

  private parse(item: any): ItemData {
    const input: [number, number, number, number] =
        [item.input.start.x, item.input.start.y, item.input.end.x, item.input.end.y];
    const tool = item.tool.ctor || item.tool;
    switch (tool) {
      case "Line": {
        const firstChars = this.getEdgeChar(item, true);
        const lastChars = this.getEdgeChar(item, false);
        return this.buildLine(input, firstChars, lastChars);
      }
      case "Rect": {
        return this.buildRect(input);
      }
      case "RectLine": {
        const firstChars = this.getEdgeChar(item, true);
        const lastChars = this.getEdgeChar(item, false);
        return this.buildRectLine(
            input,
            (item.direction._0 && item.direction._0.ctor) || item.direction,
            firstChars,
            lastChars);
      }
      default:
        return {points: [], index: {}};
    }
  }

  private shiftTo0(item: any): any {
    const x = Math.min(item.input.start.x, item.input.end.x);
    const y = Math.min(item.input.start.y, item.input.end.y);
    return Object.assign({}, item, {input: {
     start: {x: item.input.start.x - x, y: item.input.start.y - y},
     end: {x: item.input.end.x - x, y: item.input.end.y - y}
    }});
  }
}

function itemWithTextBoundingRect(item) {
  const rect = itemBoundingRect(item);
  const splittedText = item.text.split("\n");
  const widthText = Math.max.apply(null, splittedText.map((str) => str.length));
  const heightText = splittedText.length;
  if (rect.width < widthText) {
    rect.x = rect.x - Math.round(widthText / 2 - rect.width / 2);
    rect.width = widthText;
  }
  if (rect.height < heightText) {
    rect.y = rect.y - Math.round(heightText / 2 - rect.height / 2);
    rect.height = heightText;
  }
  return rect;
}

function itemBoundingRect(item) {
  const x = Math.min(item.input.start.x, item.input.end.x);
  const y = Math.min(item.input.start.y, item.input.end.y);
  const width = Math.abs(item.input.start.x - item.input.end.x);
  const height = Math.abs(item.input.start.y - item.input.end.y);
  return {x: x, y: y, width: width, height: height};
}

export const globalItem = new Item();

window["Textik"] = window["Textik"] || {};
window["Textik"]["globalItem"] = globalItem;



// WEBPACK FOOTER //
// ./assets/js/item.ts


// WEBPACK FOOTER //
// ./assets/js/item.ts